/**
 * Click Analytics Dashboard Page
 *
 * Route: /studio/admin/analytics
 *
 * Displays click event analytics with:
 * - Date range selector (7d, 30d, 90d)
 * - GroupBy toggle (day, provider, vertical)
 * - Optional vertical/provider filters
 * - Summary KPI cards (total clicks, daily average, peak day)
 * - Time series chart for daily trends
 * - Breakdown table for provider/vertical grouping
 * - Auto-refresh toggle (60s)
 *
 * Gated to users with admin:audit capability.
 */

import {
  Loader2,
  AlertCircle,
  RefreshCw,
  MousePointerClick,
  TrendingUp,
  Calendar,
  BarChart3,
  Filter,
} from "lucide-react";
import type { ReactElement } from "react";
import { useState, useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";

import { MetricCard } from "../../../components/admin/MetricCard";
import type { SparklineDataPoint } from "../../../components/admin/MetricCard";
import { TimeSeriesChart } from "../../../components/admin/TimeSeriesChart";
import type {
  TimeSeriesDataPoint,
  TimeSeriesSeries,
} from "../../../components/admin/TimeSeriesChart";
import { useClickAnalytics, getDateRange } from "../../../hooks/use-click-analytics";
import type {
  ClickGroupBy,
  DateRangePreset,
  ClickSummaryItem,
} from "../../../hooks/use-click-analytics";
import { useAuthStore } from "../../../store/authStore";

// ============================================================================
// Constants
// ============================================================================

/** Auto-refresh interval: 60 seconds */
const AUTO_REFRESH_INTERVAL = 60_000;

/** Date range preset options */
const DATE_RANGE_OPTIONS: ReadonlyArray<{ label: string; value: DateRangePreset }> = [
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "Last 90 days", value: "90d" },
];

/** GroupBy options */
const GROUP_BY_OPTIONS: ReadonlyArray<{ label: string; value: ClickGroupBy }> = [
  { label: "By Day", value: "day" },
  { label: "By Provider", value: "provider" },
  { label: "By Vertical", value: "vertical" },
];

// ============================================================================
// Main Component
// ============================================================================

export function ClickAnalyticsDashboardPage(): ReactElement {
  const { hasCapability } = useAuthStore();

  if (!hasCapability("admin:audit")) {
    return <Navigate to="/studio" replace />;
  }

  return <ClickAnalyticsDashboardContent />;
}

function ClickAnalyticsDashboardContent(): ReactElement {
  // State
  const [dateRange, setDateRange] = useState<DateRangePreset>("30d");
  const [groupBy, setGroupBy] = useState<ClickGroupBy>("day");
  const [verticalFilter, setVerticalFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Build query params
  const { startDate, endDate } = useMemo(() => getDateRange(dateRange), [dateRange]);

  const params = useMemo(
    () => ({
      startDate,
      endDate,
      groupBy,
      ...(verticalFilter.trim() ? { verticalKey: verticalFilter.trim() } : {}),
      ...(providerFilter.trim() ? { providerId: providerFilter.trim() } : {}),
    }),
    [startDate, endDate, groupBy, verticalFilter, providerFilter]
  );

  const { items, isLoading, isError, error, refetch } = useClickAnalytics(params, {
    refetchInterval: autoRefresh ? AUTO_REFRESH_INTERVAL : false,
  });

  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  // Compute KPIs
  const kpis = useMemo(() => computeKpis(items, dateRange), [items, dateRange]);

  // Chart data
  const chartData = useMemo(() => buildChartData(items, groupBy), [items, groupBy]);

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Click Analytics</h1>
          <p className="mt-1 text-foreground-muted">
            Track click-out events across verticals and providers
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Auto-refresh toggle */}
          <label className="flex items-center gap-2 text-sm text-foreground-muted">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-border"
            />
            Auto-refresh
          </label>
          <button onClick={handleRefresh} className="btn btn-secondary" disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        {/* Date range */}
        <div>
          <label className="block text-xs font-medium text-foreground-muted mb-1">
            <Calendar className="inline h-3 w-3 mr-1" />
            Date Range
          </label>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRangePreset)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            aria-label="Date range"
          >
            {DATE_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* GroupBy */}
        <div>
          <label className="block text-xs font-medium text-foreground-muted mb-1">
            <BarChart3 className="inline h-3 w-3 mr-1" />
            Group By
          </label>
          <div className="flex gap-1 rounded-lg bg-background-secondary p-1" role="tablist">
            {GROUP_BY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                role="tab"
                aria-selected={groupBy === opt.value}
                onClick={() => setGroupBy(opt.value)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  groupBy === opt.value
                    ? "bg-background text-foreground shadow-sm"
                    : "text-foreground-muted hover:text-foreground"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Vertical filter */}
        <div>
          <label className="block text-xs font-medium text-foreground-muted mb-1">
            <Filter className="inline h-3 w-3 mr-1" />
            Vertical
          </label>
          <input
            type="text"
            value={verticalFilter}
            onChange={(e) => setVerticalFilter(e.target.value)}
            placeholder="All verticals"
            className="rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary w-40"
          />
        </div>

        {/* Provider filter */}
        <div>
          <label className="block text-xs font-medium text-foreground-muted mb-1">
            <Filter className="inline h-3 w-3 mr-1" />
            Provider
          </label>
          <input
            type="text"
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            placeholder="All providers"
            className="rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary w-40"
          />
        </div>
      </div>

      {/* Content */}
      {isLoading && items.length === 0 ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={error ?? "Failed to load analytics"} onRetry={handleRefresh} />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* KPI Cards */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Total Clicks"
              value={kpis.totalClicks}
              icon={<MousePointerClick className="h-4 w-4" />}
              sparkline={kpis.sparkline}
              sparklineColor="primary"
            />
            <MetricCard
              label="Daily Average"
              value={kpis.dailyAverage}
              icon={<TrendingUp className="h-4 w-4" />}
              description={`Over ${dateRange === "7d" ? "7" : dateRange === "30d" ? "30" : "90"} days`}
            />
            <MetricCard
              label="Peak Day"
              value={kpis.peakDay.clicks}
              icon={<Calendar className="h-4 w-4" />}
              description={kpis.peakDay.date || "N/A"}
            />
            <MetricCard
              label="Unique Buckets"
              value={kpis.uniqueBuckets}
              icon={<BarChart3 className="h-4 w-4" />}
              description={`Grouped by ${groupBy}`}
            />
          </div>

          {/* Chart */}
          <div className="mb-6 card p-4">
            <h3 className="text-sm font-semibold mb-4">Click Trend</h3>
            {chartData.series ? (
              <TimeSeriesChart
                series={chartData.series}
                height={280}
                showLegend={true}
                showGrid={true}
                showAxisLabels={true}
                formatTime={formatDate}
                emptyMessage="No click data for selected period"
              />
            ) : (
              <TimeSeriesChart
                data={chartData.data ?? []}
                color="primary"
                height={280}
                showGrid={true}
                showAxisLabels={true}
                formatTime={formatDate}
                emptyMessage="No click data for selected period"
              />
            )}
          </div>

          {/* Breakdown Table */}
          <div className="card overflow-hidden">
            <div className="border-b border-border bg-background px-4 py-3">
              <h3 className="text-sm font-semibold">
                Breakdown by{" "}
                {groupBy === "day" ? "Date" : groupBy === "provider" ? "Provider" : "Vertical"}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      {groupBy === "day"
                        ? "Date"
                        : groupBy === "provider"
                          ? "Provider"
                          : "Vertical"}
                    </th>
                    {groupBy !== "day" && (
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                        Date
                      </th>
                    )}
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      Clicks
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((item, i) => (
                    <tr key={i} className="hover:bg-background-secondary transition-colors">
                      <td className="px-4 py-3 text-sm font-medium">
                        {groupBy === "day"
                          ? item.bucket
                          : groupBy === "provider"
                            ? (item.providerId ?? "Unknown")
                            : (item.verticalKey ?? "Unknown")}
                      </td>
                      {groupBy !== "day" && (
                        <td className="px-4 py-3 text-sm text-foreground-muted">{item.bucket}</td>
                      )}
                      <td className="px-4 py-3 text-sm text-right font-medium">
                        {item.clickCount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-foreground-muted">
                        {kpis.totalClicks > 0
                          ? ((item.clickCount / kpis.totalClicks) * 100).toFixed(1)
                          : "0.0"}
                        %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// KPI Computation
// ============================================================================

interface Kpis {
  totalClicks: number;
  dailyAverage: number;
  peakDay: { date: string; clicks: number };
  uniqueBuckets: number;
  sparkline: readonly SparklineDataPoint[];
}

function computeKpis(items: readonly ClickSummaryItem[], preset: DateRangePreset): Kpis {
  const totalClicks = items.reduce((sum, item) => sum + item.clickCount, 0);
  const days = preset === "7d" ? 7 : preset === "30d" ? 30 : 90;
  const dailyAverage = days > 0 ? Math.round(totalClicks / days) : 0;

  // Aggregate by bucket to find peak day
  const bucketMap = new Map<string, number>();
  for (const item of items) {
    const existing = bucketMap.get(item.bucket) ?? 0;
    bucketMap.set(item.bucket, existing + item.clickCount);
  }

  let peakDay = { date: "", clicks: 0 };
  for (const [date, clicks] of bucketMap) {
    if (clicks > peakDay.clicks) {
      peakDay = { date, clicks };
    }
  }

  // Sparkline from bucket aggregation (sorted by date)
  const sortedBuckets = [...bucketMap.entries()].sort(([a], [b]) => a.localeCompare(b));
  const sparkline: SparklineDataPoint[] = sortedBuckets.map(([, value]) => ({ value }));

  return {
    totalClicks,
    dailyAverage,
    peakDay,
    uniqueBuckets: bucketMap.size,
    sparkline,
  };
}

// ============================================================================
// Chart Data Builder
// ============================================================================

interface ChartData {
  data?: readonly TimeSeriesDataPoint[];
  series?: readonly TimeSeriesSeries[];
}

const SERIES_COLORS = ["primary", "success", "warning", "error", "info", "purple", "teal"] as const;

function buildChartData(items: readonly ClickSummaryItem[], groupBy: ClickGroupBy): ChartData {
  if (groupBy === "day") {
    // Simple single series: bucket -> timestamp, clickCount -> value
    const sorted = [...items].sort((a, b) => a.bucket.localeCompare(b.bucket));
    const data: TimeSeriesDataPoint[] = sorted.map((item) => ({
      timestamp: item.bucket,
      value: item.clickCount,
    }));
    return { data };
  }

  // Multi-series: group by provider or vertical, each gets its own series
  const seriesMap = new Map<string, TimeSeriesDataPoint[]>();

  for (const item of items) {
    const key =
      groupBy === "provider" ? (item.providerId ?? "Unknown") : (item.verticalKey ?? "Unknown");

    let points = seriesMap.get(key);
    if (!points) {
      points = [];
      seriesMap.set(key, points);
    }
    points.push({ timestamp: item.bucket, value: item.clickCount });
  }

  // Sort each series by date
  for (const points of seriesMap.values()) {
    points.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  }

  // Take top 7 series by total clicks to avoid chart clutter
  const seriesWithTotals = [...seriesMap.entries()].map(([key, points]) => ({
    key,
    points,
    total: points.reduce((sum, p) => sum + p.value, 0),
  }));
  seriesWithTotals.sort((a, b) => b.total - a.total);
  const topSeries = seriesWithTotals.slice(0, 7);

  const series: TimeSeriesSeries[] = topSeries.map((s, i) => ({
    id: s.key,
    name: s.key,
    data: s.points,
    color: SERIES_COLORS[i % SERIES_COLORS.length] ?? "primary",
  }));

  return { series };
}

// ============================================================================
// Helpers
// ============================================================================

function formatDate(timestamp: string): string {
  // Bucket is YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(timestamp)) {
    const parts = timestamp.split("-");
    return `${parts[1]}/${parts[2]}`;
  }
  return new Date(timestamp).toLocaleDateString([], { month: "short", day: "numeric" });
}

// ============================================================================
// State Components
// ============================================================================

function LoadingState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading click analytics...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
        <AlertCircle className="h-6 w-6 text-error" />
      </div>
      <p className="mt-4 text-foreground-muted">{message}</p>
      <button onClick={onRetry} className="btn btn-secondary mt-4">
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </button>
    </div>
  );
}

function EmptyState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-secondary">
        <MousePointerClick className="h-8 w-8 text-foreground-muted" />
      </div>
      <h3 className="mt-4 font-semibold">No click data found</h3>
      <p className="mt-2 text-foreground-muted">
        Click events will appear here as users interact with comparison results.
      </p>
    </div>
  );
}
